// Dependency-free snapshot-blob codec + slice builder. Imported by three places
// that must agree byte-for-byte: the client bootstrap (apply), the integration
// parity test, and the atsume-scraper build job (serialize). Keep it free of any
// Vue / Capacitor / app-store import so the scraper can pull this one file in.
import { ENTITY_REPLICA_CONFIGS } from '../../data/entityReplicaConfig.generated.ts'

// Bumped only if the on-disk blob framing changes (NDJSON layout), independent of
// the per-entity REPLICA_VERSION projection contract carried in the manifest.
export const BLOB_FORMAT_VERSION = 1

// The shared blob holds every type-NULL row (lookups + deep graph). We obtain it
// from the live feed by subscribing to a sentinel type that matches no real
// medium: the resolver's `type IN (sentinel) OR type IS NULL` then returns only
// the NULL-type rows, i.e. exactly the always-sync slice — no guessing which
// entities are shared, the server filter decides.
export const SHARED_BLOB_KEY = 'shared'
export const SHARED_SENTINEL_TYPE = '__atsume_shared_only__'

type Tombstone = { entity: string; rowId: string }
type BlobItem = { id?: unknown } & Record<string, unknown>

// Same structural shape applyDelta consumes: one array per entity field plus the
// shared tombstone list. The drain's paging fields (nextSeq/headSeq/...) are
// stripped before serialization — only the payload is stored.
export type BlobDeltaPage = { tombstones: Tombstone[] } & Record<string, unknown>

export type FeedPage = BlobDeltaPage & {
  nextSeq: string
  headSeq: string
  hasMore: boolean
  resyncRequired?: boolean
}

export type PullFeed = (
  sinceSeq: string,
  subscription: { types?: string[]; regions: string[] },
) => Promise<FeedPage | null>

export type BlobManifestEntry = {
  key: string
  file: string
  bytes: number
  sha256: string
  entities: string[]
}

export type BlobManifest = {
  formatVersion: number
  builtAtSeq: string
  region: string
  entityVersions: Record<string, number>
  blobs: BlobManifestEntry[]
}

const ENTITY_FIELDS = ENTITY_REPLICA_CONFIGS.map((config) => config.field)
const ENTITY_BY_FIELD = new Map(ENTITY_REPLICA_CONFIGS.map((config) => [config.field, config.entity]))

export const ENTITY_VERSIONS: Record<string, number> = Object.fromEntries(
  ENTITY_REPLICA_CONFIGS.map((config) => [config.entity, config.version]),
)

function itemsOf(page: BlobDeltaPage, field: string): BlobItem[] {
  const value = page[field]
  return Array.isArray(value) ? (value as BlobItem[]) : []
}

function rowKey(entity: string, id: unknown): string {
  return `${entity}:${String(id)}`
}

// Strip the drain's paging fields, keeping only the entity arrays + tombstones —
// the minimal payload applyDelta needs, so blobs don't store nextSeq noise.
function toPayload(page: BlobDeltaPage): BlobDeltaPage {
  const payload: BlobDeltaPage = { tombstones: page.tombstones ?? [] }
  for (const field of ENTITY_FIELDS) {
    const items = itemsOf(page, field)
    if (items.length > 0) payload[field] = items
  }
  return payload
}

function isEmptyPayload(page: BlobDeltaPage): boolean {
  if ((page.tombstones ?? []).length > 0) return false
  return ENTITY_FIELDS.every((field) => itemsOf(page, field).length === 0)
}

// Total upsert + tombstone rows a page carries — drives row-based apply progress.
export function pageRowCount(page: BlobDeltaPage): number {
  let count = (page.tombstones ?? []).length
  for (const field of ENTITY_FIELDS) count += itemsOf(page, field).length
  return count
}

// Every (entity, id) carried by a page — used to subtract the shared slice from a
// per-type slice so the type blob only ships rows the shared blob doesn't.
export function pageRowKeys(page: BlobDeltaPage): string[] {
  const keys: string[] = []
  for (const field of ENTITY_FIELDS) {
    const entity = ENTITY_BY_FIELD.get(field)
    if (!entity) continue
    for (const item of itemsOf(page, field)) keys.push(rowKey(entity, item.id))
  }
  for (const tombstone of page.tombstones ?? []) keys.push(rowKey(tombstone.entity, tombstone.rowId))
  return keys
}

function excludeKeys(page: BlobDeltaPage, shared: Set<string>): BlobDeltaPage {
  const filtered: BlobDeltaPage = {
    tombstones: (page.tombstones ?? []).filter((t) => !shared.has(rowKey(t.entity, t.rowId))),
  }
  for (const field of ENTITY_FIELDS) {
    const entity = ENTITY_BY_FIELD.get(field)
    if (!entity) continue
    const kept = itemsOf(page, field).filter((item) => !shared.has(rowKey(entity, item.id)))
    if (kept.length > 0) filtered[field] = kept
  }
  return filtered
}

function entitiesPresent(pages: BlobDeltaPage[]): string[] {
  const present = new Set<string>()
  for (const page of pages) {
    for (const field of ENTITY_FIELDS) {
      if (itemsOf(page, field).length > 0) present.add(ENTITY_BY_FIELD.get(field) ?? field)
    }
    for (const tombstone of page.tombstones ?? []) present.add(tombstone.entity)
  }
  return [...present].sort()
}

async function drain(
  pull: PullFeed,
  subscription: { types?: string[]; regions: string[] },
): Promise<{ pages: BlobDeltaPage[]; headSeq: number }> {
  const pages: BlobDeltaPage[] = []
  let since = '0'
  let headSeq = Number.POSITIVE_INFINITY
  for (;;) {
    const page = await pull(since, subscription)
    if (!page) break
    if (page.resyncRequired) throw new Error('resyncRequired during blob build — retention gap')
    headSeq = Math.min(headSeq, Number(page.headSeq))
    const payload = toPayload(page)
    if (!isEmptyPayload(payload)) pages.push(payload)
    since = page.nextSeq
    if (!page.hasMore) break
  }
  return { pages, headSeq: Number.isFinite(headSeq) ? headSeq : 0 }
}

export type BlobSet = {
  builtAtSeq: string
  region: string
  blobs: { key: string; pages: BlobDeltaPage[]; entities: string[] }[]
}

// The global feed head, read once before any slice drain (no type filter ⇒ max seq
// across every replicated entity). This is builtAtSeq: because each slice below is
// drained to completion, the blobs contain every change with seq <= this head for
// the subscribed slices, so a client can safely start its cursor here. Read BEFORE
// draining (not after) so a change landing mid-build gets a seq > builtAtSeq and is
// still pulled by forward sync rather than skipped.
async function readGlobalHead(pull: PullFeed, region: string): Promise<number> {
  const page = await pull('0', { regions: [region] })
  return page ? Number(page.headSeq) : 0
}

// Drain the feed into composable slices. The shared blob = the NULL-type slice;
// each per-type blob = that type's slice minus whatever the shared blob already
// carries. builtAtSeq = the global head (see readGlobalHead) — NOT the min per-slice
// head, which an asymmetric backfill (advancing only some slices) would drag back to
// a stale watermark and force the client into a redundant full forward re-drain.
export async function buildBlobSet(opts: {
  pull: PullFeed
  types: string[]
  region: string
}): Promise<BlobSet> {
  const builtAtSeq = await readGlobalHead(opts.pull, opts.region)
  const shared = await drain(opts.pull, {
    types: [SHARED_SENTINEL_TYPE],
    regions: [opts.region],
  })

  const sharedKeys = new Set<string>()
  for (const page of shared.pages) for (const key of pageRowKeys(page)) sharedKeys.add(key)

  const blobs: BlobSet['blobs'] = [
    { key: SHARED_BLOB_KEY, pages: shared.pages, entities: entitiesPresent(shared.pages) },
  ]

  for (const type of opts.types) {
    const slice = await drain(opts.pull, { types: [type], regions: [opts.region] })
    const typePages = slice.pages
      .map((page) => excludeKeys(page, sharedKeys))
      .filter((page) => !isEmptyPayload(page))
    blobs.push({ key: type, pages: typePages, entities: entitiesPresent(typePages) })
  }

  return {
    builtAtSeq: String(builtAtSeq),
    region: opts.region,
    blobs,
  }
}

export function serializePages(pages: BlobDeltaPage[]): string {
  return pages.map((page) => JSON.stringify(page)).join('\n')
}

export function* parsePages(text: string): Generator<BlobDeltaPage> {
  for (const line of text.split('\n')) {
    const trimmed = line.trim()
    if (trimmed.length === 0) continue
    yield JSON.parse(trimmed) as BlobDeltaPage
  }
}
